import React from 'react';
import { useAppSelector, useAppDispatch } from '../store/hooks';
import { 
  selectFilteredEnterprises, 
  selectFollowedEnterprises,
  selectSearchQuery,
  selectLoading,
  selectError,
  setSearchQuery,
  fetchEnterprises,
  followEnterpriseAsync,
  unfollowEnterpriseAsync
} from '../store/slices/enterpriseSlice';
import PageTemplate from '../components/templates/PageTemplate';
import SearchBar from '../components/molecules/SearchBar';
import EnterpriseCardComponent from '../components/organisms/EnterpriseCard';
import { MobileHeaderType } from '../components/layout/ConfigurableMobileHeader';
import styled from 'styled-components';

const PageContent = styled.div`
  max-width: 800px;
  margin: 0 auto;
  padding: 0 24px;
  
  @media (max-width: ${({ theme }) => theme.breakpoints.tablet}) {
    padding: 0;
    max-width: 100%;
    overflow-x: hidden;
  }
`;

const EnterpriseList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  margin-top: 32px;
  
  @media (max-width: ${({ theme }) => theme.breakpoints.tablet}) {
    margin-top: 16px;
    gap: 8px;
  }
`;

const LoadingContainer = styled.div`
  padding: 24px;
  text-align: center;
  color: ${({ theme }) => theme.colors.text.secondary};
  font-size: 16px;
`;

const ErrorContainer = styled.div`
  padding: 24px;
  text-align: center;
  color: #f44336;
  font-size: 16px;
  background-color: rgba(244, 67, 54, 0.1);
  border-radius: 8px;
  margin-bottom: 24px;
`;

const CreatorStats = styled.div`
  display: flex;
  gap: 24px;

  div {
    text-align: center;

    .count {
      color: #fff;
      font-size: 20px;
      font-weight: 600;
      margin-bottom: 4px;
    }

    .label {
      color: rgba(255, 255, 255, 0.6);
      font-size: 14px;
    }
  }
  
  @media (max-width: ${({ theme }) => theme.breakpoints.tablet}) {
    gap: 16px;
    
    div .count {
      font-size: 18px;
    }
    
    div .label {
      font-size: 12px;
    }
  }
`;

const EarningsStats = styled.div`
  h4 {
    color: rgba(255, 255, 255, 0.6);
    font-size: 14px;
    font-weight: 400;
    margin: 0 0 8px 0;
  }

  .amount {
    color: #fff;
    font-size: 24px;
    font-weight: 600;
  }

  .period {
    color: rgba(255, 255, 255, 0.6);
    font-size: 14px;
  }
  
  @media (max-width: ${({ theme }) => theme.breakpoints.tablet}) {
    h4 {
      font-size: 13px;
    }
    
    .amount {
      font-size: 20px;
    }
    
    .period {
      font-size: 12px;
    }
  }
`;

const StatsContainer = styled.div`
  display: grid;
  grid-template-columns: 1fr auto;
  gap: 24px;
  margin-bottom: 24px;
  
  @media (max-width: ${({ theme }) => theme.breakpoints.tablet}) {
    grid-template-columns: 1fr;
    gap: 16px;
    margin-bottom: 16px;
  }
`;

const SubmitSection = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px;
  background-color: #131314;
  border-radius: 8px;

  .submit-info {
    display: flex;
    align-items: center;
    gap: 12px;

    img {
      width: 24px;
      height: 24px;
    }

    div {
      h4 {
        color: #fff;
        font-size: 15px;
        margin: 0 0 4px 0;
      }

      span {
        color: rgba(255, 255, 255, 0.6);
        font-size: 13px;
      }
    }
  }
  
  @media (max-width: ${({ theme }) => theme.breakpoints.tablet}) {
    flex-direction: column;
    align-items: flex-start;
    gap: 12px;
    padding: 12px;
    
    button {
      width: 100%;
    }
  }
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 12px;
  
  @media (max-width: ${({ theme }) => theme.breakpoints.tablet}) {
    width: 100%;
    justify-content: space-between;
    
    button {
      flex: 1;
    }
  }
`;

const Button = styled.button<{ variant?: 'primary' | 'secondary' }>`
  padding: 8px 16px;
  border-radius: 6px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;

  ${({ variant }) =>
    variant === 'primary'
      ? `
    background-color: #9333EA;
    color: #fff;
    border: none;

    &:hover {
      background-color: #7C3AED;
    }
  `
      : `
    background-color: transparent;
    color: #fff;
    border: 1px solid rgba(255, 255, 255, 0.1);

    &:hover {
      border-color: rgba(255, 255, 255, 0.2);
    }
  `}
`;

const EnterpriseHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 24px;
  
  @media (max-width: ${({ theme }) => theme.breakpoints.tablet}) {
    flex-direction: column;
    align-items: flex-start;
    gap: 12px;
    margin-bottom: 16px;
  }
`;

const EnterpriseInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;

  img {
    width: 48px;
    height: 48px;
    border-radius: 8px;
  }
`;

const EnterpriseDetails = styled.div`
  h3 {
    color: #fff;
    font-size: 18px;
    font-weight: 500;
    margin: 0 0 4px 0;
  }

  span {
    color: rgba(255, 255, 255, 0.6);
    font-size: 14px;
  }
`;

const EnterpriseCard = styled.div`
  background-color: #131314;
  border-radius: 12px;
  padding: 24px;
  margin-bottom: 16px;
  transition: transform 0.2s ease, box-shadow 0.2s ease;
  border: 1px solid rgba(255, 255, 255, 0.05);

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 8px 24px rgba(0, 0, 0, 0.2);
    border-color: rgba(255, 255, 255, 0.1);
  }
  
  @media (max-width: ${({ theme }) => theme.breakpoints.tablet}) {
    padding: 16px;
    margin-bottom: 12px;
  }
`;

const PageHeader = styled.div`
  margin-bottom: 24px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  
  @media (max-width: ${({ theme }) => theme.breakpoints.tablet}) {
    display: none; /* Hide on mobile as we have tabs in MobileHeader */
  }
`;

const PageTitle = styled.h1`
  font-size: 28px;
  font-weight: 600;
  color: #fff;
  margin: 0;
`;

const SearchContainer = styled.div`
  width: 100%;
  max-width: 100%;
  margin-bottom: 24px;
  
  @media (max-width: ${({ theme }) => theme.breakpoints.tablet}) {
    margin-bottom: 8px;
    padding: 0 8px;
  }
`;

const EmptyState = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 48px 0;
  text-align: center;
  
  img {
    width: 120px;
    height: 120px;
    margin-bottom: 24px;
  }
  
  h3 {
    font-size: 20px;
    font-weight: 600;
    color: #fff;
    margin: 0 0 8px 0;
  }
  
  p {
    font-size: 16px;
    color: rgba(255, 255, 255, 0.6);
    margin: 0 0 24px 0;
    max-width: 400px;
  }
`;

const Enterprises: React.FC = () => {
  const dispatch = useAppDispatch();
  const enterprises = useAppSelector(selectFilteredEnterprises);
  const followedEnterprises = useAppSelector(selectFollowedEnterprises);
  const searchQuery = useAppSelector(selectSearchQuery);
  const loading = useAppSelector(selectLoading);
  const error = useAppSelector(selectError);
  
  // Fetch enterprises when component mounts
  React.useEffect(() => {
    dispatch(fetchEnterprises());
  }, [dispatch]);

  const handleFollow = (enterpriseId: string) => {
    if (followedEnterprises.includes(enterpriseId)) {
      dispatch(unfollowEnterpriseAsync(enterpriseId));
    } else {
      dispatch(followEnterpriseAsync(enterpriseId));
    }
  };

  const handleSubmitVideo = (enterpriseName: string) => {
    // In a real app, this would open a video submission form/modal
    alert(`Opening video submission form for ${enterpriseName}`);
  };

  const handleDetails = (enterpriseId: string) => {
    // In a real app, this would navigate to the enterprise details page
    alert(`Navigating to ${enterpriseId} details page`);
  };

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    dispatch(setSearchQuery(e.target.value));
  };

  return (
    <PageTemplate
      hideHeaderOnMobile={true}
      mobileHeaderType={MobileHeaderType.FULL}
      mobileHeaderTitle="Enterprises"
    >
      <PageContent>
        <SearchContainer>
          <SearchBar 
            value={searchQuery}
            onChange={handleSearchChange}
            placeholder="Search Enterprises or categories"
          />
        </SearchContainer>
        
        {loading && (
          <LoadingContainer>Loading enterprises...</LoadingContainer>
        )}
        
        {error && (
          <ErrorContainer>Error: {error}</ErrorContainer>
        )}

        {!loading && !error && enterprises.length === 0 && (
          <EmptyState>
            <img src="/images/empty-enterprises.svg" alt="No enterprises found" />
            <h3>No enterprises found</h3>
            <p>Try adjusting your search or check back later for new enterprises</p>
          </EmptyState>
        )}

        <EnterpriseList>
          {enterprises.map((enterprise) => (
            <EnterpriseCardComponent 
              key={enterprise.id}
              enterprise={enterprise}
              isFollowed={followedEnterprises.includes(enterprise.id)}
              onFollow={handleFollow}
              onDetails={handleDetails}
              onSubmitVideo={handleSubmitVideo}
            />
          ))}
        </EnterpriseList>
      </PageContent>
    </PageTemplate>
  );
};

export default Enterprises;
